import { useState, useEffect, useCallback, useRef } from 'react'
import { getSentWeeks, deleteSentWeek, exportExcel, importScheduleExcel } from '../api/client'
import { formatWeekRange, getMonday, toISODate } from '../utils/weeks'
import EditWeekModal from '../components/EditWeekModal'
import { useTranslation } from 'react-i18next'
import { currentLocale } from '../i18n'

function formatSentAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString(currentLocale(), {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

function getPastMondays(count = 52) {
  const mondays = []
  // getMonday zeroes the time and toISODate formats in LOCAL time. The
  // previous version kept today's clock time and called toISOString(), which
  // converts to UTC -- so any afternoon in Chile rolled the date forward and
  // stored a Tuesday as the week start. Every week saved that way is keyed a
  // day off, and week_start is a Monday everywhere else in the system.
  const lastMonday = getMonday(new Date())
  for (let i = 0; i < count; i++) {
    const d = new Date(lastMonday)
    d.setDate(lastMonday.getDate() - i * 7)
    mondays.push(toISODate(d))
  }
  return mondays
}

function formatWeekLabel(isoDate) {
  const d = new Date(isoDate + 'T00:00:00')
  const end = new Date(d)
  end.setDate(d.getDate() + 4)
  const fmt = (dt) => dt.toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric' })
  return `${fmt(d)} – ${fmt(end)}, ${d.getFullYear()}`
}

function UploadModal({ onClose, onSuccess }) {
  const { t } = useTranslation('engineering')
  const pastMondays = getPastMondays(52)
  const [selectedWeek, setSelectedWeek] = useState(pastMondays[1] || pastMondays[0])
  const [file, setFile] = useState(null)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef()

  const handleSubmit = async () => {
    if (!file) { setError(t('schedule.upload.noFile')); return }
    setImporting(true)
    setError('')
    try {
      const result = await importScheduleExcel(selectedWeek, file)
      onSuccess(result)
    } catch (err) {
      setError(err.response?.data?.detail || 'Import failed')
    }
    setImporting(false)
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">{t('schedule.upload.title')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.upload.weekLabel')}</label>
            <select
              value={selectedWeek}
              onChange={e => setSelectedWeek(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              {pastMondays.map(iso => (
                <option key={iso} value={iso}>{formatWeekLabel(iso)}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('schedule.upload.excelLabel')}</label>
            <div
              className="border-2 border-dashed border-gray-200 rounded-lg px-4 py-5 text-center cursor-pointer hover:border-gray-400 transition-colors"
              onClick={() => fileRef.current?.click()}
            >
              {file ? (
                <p className="text-sm text-gray-700 font-medium">{file.name}</p>
              ) : (
                <p className="text-sm text-gray-400">{t('schedule.upload.clickToSelect')}</p>
              )}
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={e => { setFile(e.target.files[0] || null); setError('') }}
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('schedule.upload.sameFormat')}</p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100">{error}</p>
          )}
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-200 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('schedule.upload.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={importing}
            className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            {importing ? t('schedule.upload.importing') : t('schedule.upload.import')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default function Schedule() {
  const { t } = useTranslation('engineering')
  const [weeks, setWeeks] = useState([])
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [importResult, setImportResult] = useState(null)
  const [editWeek, setEditWeek] = useState(null)
  const [downloadingAll, setDownloadingAll] = useState(false)
  const [downloadError, setDownloadError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await getSentWeeks()
      setWeeks(data)
    } catch {
      setWeeks([])
    }
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const handleDelete = async (id) => {
    if (!confirm(t('schedule.deleteConfirm'))) return
    await deleteSentWeek(id)
    await load()
  }

  // One workbook covering every sent week: Summary plus a sheet per week.
  const handleDownloadAll = async () => {
    if (weeks.length === 0) return
    const starts = weeks.map(w => w.week_start).sort()
    setDownloadingAll(true)
    setDownloadError('')
    try {
      await exportExcel(starts[0], starts[starts.length - 1], { skipEmptyWeeks: true })
    } catch {
      setDownloadError(t('schedule.downloadAllFailed'))
    }
    setDownloadingAll(false)
  }

  const handleUploadSuccess = async (result) => {
    setShowUpload(false)
    setImportResult(result)
    await load()
  }

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div className="min-w-0">
          <h1 className="text-[28px] font-bold text-gray-900 tracking-tight leading-tight">{t('schedule.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('schedule.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          {!loading && weeks.length > 0 && (
            <button
              onClick={handleDownloadAll}
              disabled={downloadingAll}
              className="bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              {downloadingAll ? t('schedule.downloadingAll') : t('schedule.downloadAll')}
            </button>
          )}
          <button
            onClick={() => { setImportResult(null); setShowUpload(true) }}
            className="border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('schedule.uploadPastWeek')}
          </button>
          <button
            onClick={load}
            className="border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            {t('schedule.refresh')}
          </button>
        </div>
      </div>

      {downloadError && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-red-600 text-sm border border-red-100">
          {downloadError}
        </div>
      )}

      {importResult && (
        <div className="mb-4 px-4 py-3 rounded-xl bg-emerald-50 text-emerald-700 text-sm border border-emerald-100">
          {t('schedule.importedAllocations', {count: importResult.allocations_imported, week: importResult.week_start})}
        </div>
      )}

      {loading ? (
        <div className="text-gray-400 text-sm">{t('schedule.loading')}</div>
      ) : weeks.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p className="text-sm">{t('schedule.noWeeksYet')}</p>
          <p className="text-xs mt-1">{t('schedule.noWeeksHint')}</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
          {weeks.map(w => (
            <div key={w.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
              <div className="w-10 h-10 rounded-full bg-gray-100 text-gray-500 flex items-center justify-center shrink-0">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">{formatWeekRange(w.week_start)}</div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {t('schedule.sent')} {formatSentAt(w.sent_at)}
                  {w.recipient_count > 0 && (
                    <> · {t('schedule.recipient', {count: w.recipient_count})}</>
                  )}
                </div>
              </div>
              <button
                onClick={() => exportExcel(w.week_start, w.week_start)}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-700 transition-colors shrink-0"
              >
                ↓ Excel
              </button>
              <button
                onClick={() => setEditWeek(w.week_start)}
                title={t('schedule.editWeekTitle')}
                aria-label={t('schedule.editWeekAriaLabel')}
                className="text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg p-2 transition-colors shrink-0"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
              <button
                onClick={() => handleDelete(w.id)}
                title={t('schedule.removeFromList')}
                aria-label={t('schedule.removeFromList')}
                className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg p-2 transition-colors shrink-0"
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {editWeek && (
        <EditWeekModal
          week={editWeek}
          onClose={() => setEditWeek(null)}
          onSaved={load}
        />
      )}
    </div>
  )
}
